import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createApiRequest } from '../../utils/apiUtils';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  FormControlLabel,
  Switch,
  IconButton,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

interface PaymentMethod {
  code: string;
  label: string;
  enabled: boolean;
}

interface PaymentMethodsResponse {
  methods: PaymentMethod[];
}

const CODE_OTHER = 'OTHER';

// createApiRequest はHTTPエラー時にレスポンス本文(JSON文字列)を Error.message に入れる。
const extractMessage = (e: unknown): string => {
  const raw = e instanceof Error ? e.message : String(e);
  try {
    const j = JSON.parse(raw);
    if (j && typeof j.message === 'string') return j.message;
  } catch {
    /* not JSON */
  }
  return raw || '操作に失敗しました。';
};

/**
 * 会計方法セクション。会計リクエスト機能（feast-be ADR 0107）。
 * 顧客が会計時に選べる支払方法（現金/クレカ/PayPay/交通系IC/QR＋店舗独自）を店舗が設定する。
 * BE: GET/PUT /stores/{storeId}/payment-methods。編集は MANAGER のみ。
 */
const PaymentMethodSection: React.FC = () => {
  const { store_id } = useParams<{ store_id: string }>();
  const isManager = localStorage.getItem('role') === 'ROLE_MANAGER';

  const [methods, setMethods] = useState<PaymentMethod[]>([]);
  const [editing, setEditing] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const fetchMethods = useCallback(() => {
    if (!store_id) return;
    setLoading(true);
    createApiRequest<PaymentMethodsResponse>(`/stores/${store_id}/payment-methods`, { method: 'GET' })
      .then((data) => setMethods(data.methods || []))
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setLoading(false));
  }, [store_id]);

  useEffect(() => {
    fetchMethods();
  }, [fetchMethods]);

  const startEdit = () => {
    setSuccess(false);
    setError(null);
    setEditing(true);
  };

  const cancelEdit = () => {
    setEditing(false);
    setNewLabel('');
    setError(null);
    fetchMethods();
  };

  const toggleEnabled = (index: number) => {
    setMethods((prev) => prev.map((m, i) => (i === index ? { ...m, enabled: !m.enabled } : m)));
  };

  const removeMethod = (index: number) => {
    setMethods((prev) => prev.filter((_, i) => i !== index));
  };

  const addCustom = () => {
    const label = newLabel.trim();
    if (!label) return;
    setMethods((prev) => [...prev, { code: CODE_OTHER, label, enabled: true }]);
    setNewLabel('');
  };

  const save = () => {
    if (!store_id) return;
    setBusy(true);
    setError(null);
    createApiRequest<PaymentMethodsResponse>(`/stores/${store_id}/payment-methods`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ methods }),
    })
      .then((data) => {
        setMethods(data.methods || []);
        setEditing(false);
        setSuccess(true);
      })
      .catch((e) => setError(extractMessage(e)))
      .finally(() => setBusy(false));
  };

  const enabledLabels = methods.filter((m) => m.enabled).map((m) => m.label);

  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="h6">会計方法</Typography>
        {isManager && !editing && (
          <Button size="small" variant="outlined" startIcon={<EditOutlinedIcon />} onClick={startEdit}>
            編集
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        会計リクエスト時にお客様へ提示する支払方法です。有効にした方法から選べます。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && !editing && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          保存しました。
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      ) : editing ? (
        <>
          <Stack spacing={0.5}>
            {methods.map((m, index) => (
              <Box key={`${m.code}-${index}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <FormControlLabel
                  control={<Switch checked={m.enabled} onChange={() => toggleEnabled(index)} />}
                  label={m.label}
                />
                {m.code === CODE_OTHER && (
                  <IconButton aria-label="削除" size="small" onClick={() => removeMethod(index)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
            ))}
          </Stack>
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <TextField
              label="会計方法を追加"
              placeholder="例: 商品券"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addCustom();
                }
              }}
              size="small"
              fullWidth
              inputProps={{ maxLength: 64 }}
            />
            <Button variant="outlined" onClick={addCustom} disabled={!newLabel.trim()}>
              追加
            </Button>
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 2, justifyContent: 'flex-end' }}>
            <Button variant="text" color="inherit" onClick={cancelEdit} disabled={busy}>
              キャンセル
            </Button>
            <Button variant="contained" onClick={save} disabled={busy}>
              保存
            </Button>
          </Stack>
        </>
      ) : (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {enabledLabels.length > 0 ? enabledLabels.join('、') : '—'}
        </Typography>
      )}
    </Box>
  );
};

export default PaymentMethodSection;
